import {Injectable} from '@angular/core';
import {ActivatedRoute, NavigationEnd, NavigationStart, Router} from '@angular/router';
import {UserManager, WebStorageStateStore} from "@oup/oidc-client";
import {CommonService} from 'src/app/service/common.service';
import {DataService} from 'src/app/service/data.service';
import {environment} from 'src/environments/environment';
import {AlertService} from './alert.service';
import {Md5} from "ts-md5";
import {BehaviorSubject, Subscription} from 'rxjs';
import {WindowUnloadService} from './WindowUnloadService';
import moment from "moment/moment";
import {HttpHeaders} from '@angular/common/http';
import {LocationStrategy} from "@angular/common";
import {ScriptService} from "./script.service";
import * as XLSX from 'xlsx-js-style';

export class MemoryStore {
	private _store: Record<string, any>;
	private _idTokenStore: Record<string, any>;
	private static _instance: MemoryStore;

	constructor() {
		this._store = {};
		// Store ID tokens in memory instead of localStorage for better security
		this._idTokenStore = {};
	}

	// Security Fix: Store ID tokens in memory instead of localStorage
	// This prevents XSS attacks from accessing tokens via localStorage
	static getIDToken(key: string): string | null {
		// Access instance method through a singleton-like approach
		return MemoryStore._getInstance()._idTokenStore[`${key}:IDToken`] || null;
	}

	static setIDToken(key: string, value: string): void {
		// Access instance method through a singleton-like approach
		MemoryStore._getInstance()._idTokenStore[`${key}:IDToken`] = value;
	}

	// Helper method to get the current instance
	static _getInstance() {
		if (!MemoryStore._instance) {
			MemoryStore._instance = new MemoryStore();
		}
		return MemoryStore._instance;
	}

	get length() {
		return Object.keys(this._store).length;
	}

	key(index: number): string | null {
		return Object.keys(this._store)[index];
	}

	setItem(key: string, value: any): void {
		if (key.startsWith('oidc.user')) {
			const user = JSON.parse(value);
			MemoryStore.setIDToken(key, user.id_token);
		}
		this._store[key] = value;
	}

	getItem(key: string): any {
		if (key.startsWith('oidc.user') && this._store[key] == null && MemoryStore.getIDToken(key) != null) {
			return JSON.stringify({
			id_token: MemoryStore.getIDToken(key),
			});
		}

		return this._store[key];
	}

	removeItem(key: string): void {
		delete this._store[key];
	}

	clear() {
		this._store = {};
		// Also clear ID tokens when clearing the store
		this._idTokenStore = {};
	}

	// Static method to clear all data (useful for logout)
	static clearAll() {
		const instance = MemoryStore._getInstance();
		instance.clear();
	}
}
@Injectable({providedIn: 'root'})
export class OupService {

	public userManager:any = this.createUserManager(environment.oupMiddleLayer.endpoint, environment.oupMiddleLayer.clientID);
	private accessToken = null;
	//將isOupUser改為BehaviorSubject，可照舊用oups.isOupUser，亦可subscript isOupUser$
	private _isOupUser = false;
	public isOupUser$: BehaviorSubject<boolean> = new BehaviorSubject(this._isOupUser);
	public get isOupUser() {
		return this._isOupUser;
	};

	public set isOupUser(value) {
		if (this._isOupUser !== value) {
			this._isOupUser = value;
			this.isOupUser$.next(value);
		}
	};

	public oup_school_year = null;

	public entitlement_result: any = [];

	public oup_school: any = {};
	public teachers: any = [];
	public classes: any = [];
	public students: any = [];

	public teacher_map = {};
	public class_map = {};
	public student_map = {};
	/**
	 * @description uid => oup_user_id
	 * @type {Object}
	 */
	public user_id_map = {};

	public oupEulaVer: number = 1733657435;
	public oupEulaRouteTemp: string = "";

	public routerSubscriptions: Subscription[] = [];

	private _is_fetching_all_data: boolean = false;
	public _is_fetching_all_data_done: boolean = false;
	public _is_fetched_once = false;
	private _isRunningOupTokenLogin: boolean = false;
	private _isRunningLoginPostProcess: boolean = false;
	public _isLoginChecked: boolean = false;
	public _oupSsoUser:any = null;
	public _isNoEntitlement:boolean = false;

	private on_logout_callbacks: Function[] = [];
	public is_in_lesson: boolean = false;
	public role_data: any = {role: [], role_permission: []};
	private last_sync_hash = null;
	public prev_school_year_data_cache_keys = [];
	private userInfoSubscription: Subscription;

	middle_layer_api_queue: MiddleLayerApiQueue = null;

	constructor(
		private coms: CommonService,
		private datas: DataService,
		private router: Router,
		private aRoute: ActivatedRoute,
		public unloadService: WindowUnloadService,
		private als: AlertService,
		private locationStrategy: LocationStrategy,
		public script: ScriptService,
	) {
		// 如果是deeplink，直接返回true，將重新login
		if ((window as any).isDeepLink) return;

		this.middle_layer_api_queue = new MiddleLayerApiQueue();

		this.init();

		// 因與noEntitlement的流程有衝突，改為在檢查noEntitlement後才執行checkNeedShowEula()
		// this.initEula();

		this.checkIsOupUser();

		this.datas.jwt$.subscribe(() => {
			this.checkIsOupUser();
		});

		// Store the subscription when subscribing
		this.userInfoSubscription = this.datas.userInfo$.subscribe(() => {
			if (this.isOupUser) this.loginPostProcess();
		});

		this.router.events.subscribe((val) => {
			// console.log('router events', val);
			let can_fetch_data = val instanceof NavigationEnd && this.isOupUser && val.urlAfterRedirects != '/login' && val.urlAfterRedirects != '/redirect';
			// console.log('can fetch data ', can_fetch_data);
			if (can_fetch_data && !(location.port != '' && sessionStorage.getItem('oup'))) {
				this.fetch_all_data();
			}
		});
	}

	public mlClientApi: Function = async (apiName: string, data: any = {}): Promise<any> => {
		if (!this.accessToken) return Promise.reject("No oup access token");
		let id = Math.random().toString(36).substring(2);
		await this.middle_layer_api_queue.add(id);
		const oupMiddleLayerServiceEndpoint = location.protocol == "http:" ? environment.oupMiddleLayer.oupMiddleLayerServiceEndpointHttp : environment.oupMiddleLayer.oupMiddleLayerServiceEndpointHttps;
		let headers = new HttpHeaders();
		headers = headers.set('oupToken', this.accessToken);
		let option:any = { headers: headers };
		return this.datas.post2({
			url: oupMiddleLayerServiceEndpoint + apiName,
			option: option,
			jwt: false,
			data: data
		}).finally(() => {
			this.middle_layer_api_queue.finish(id);
		});
	}

	get isOupSchool() {
		return !!(this.isOupUser || ["272"].includes(`${this.datas.userInfo?.school_id}`) || ["oup", "OUP"].includes(`${this.datas.userInfo?.school.prefix}`));
	}

	// for oup account without 272
	get isOupAccount() {
		return !!(this.isOupUser || ["oup", "OUP"].includes(`${this?.datas?.userInfo?.school?.prefix}`));

	}

	private createUserManager(endpoint: string, clientID: string): UserManager {
		const currentUrl = window.location.href;
		const url = new URL(currentUrl);

		return new UserManager({
			authority: `${endpoint}/auth/v1`,
			client_id: clientID,
			redirect_uri: `${url.origin}/redirect`,
			popup_redirect_uri: `${url.origin}/redirect`,
			post_logout_redirect_uri: `${url.origin}/login`,
			response_type: 'code',
			scope: 'openid',
			// Set automaticSilentRenew to true
			// The client library would renew access token with refresh token when the access token is about to expire
			//
			// When user is idle for one minute, the renewal would be paused
			// It would resume once the user is active again
			automaticSilentRenew: true,
			accessTokenExpiringNotificationTime: 60,
			// Uncomment next line to customize the idle time of pausing token renewal, 60 * 1000 = 60s
			// pauseSilentRenewIdleTimeout: 60 * 1000
			userStore: new WebStorageStateStore({ store: new MemoryStore() })
		});
	}

	public init(): void {
		if (location.port != '' && sessionStorage.getItem('oup')) {
			return;
		}

		this.initIdleTimer();
	}

	public checkIsOupUser(): Promise<boolean> {
		return new Promise(async (resolve, reject) => {
			try {
				if (this.isOupUser) {
					resolve(true);
					return;
				}
				await this.coms.waitFor(() => this.datas && this.datas.userInfo && this.datas.userInfo?.school);
				if (sessionStorage.getItem('oup') && location.port != '') {
					resolve(true);
					return;
				}
				if (this.datas.userInfo?.school.plan_type == 'oup' && !this.isOupUser) {
					this.isOupUser = true;

					let user = await this.userManager.getUser();
					if (user && !user.expired) {
						//如果token未expire，可繼續用
						this.accessToken = user.access_token;
						this._oupSsoUser = user;

						this.oupTokenLogin(
							user.profile["https://oupchina.com.hk/school_id"],
							user.profile.userID,
							user.profile["https://oupchina.com.hk/user_type"],
							user.access_token
						);
					} else {
						// 不知道為什麼複製 url 在新 tab 開啟時，user 會是 null
						await this.coms.waitFor(() => this.router.url && this.router.url != "/");
						await this.userManager.signinRedirect({
							prompt: 'none',
							state: this.router.url
						});
					}
				} else if (this.datas.userInfo?.school.plan_type != 'oup' && this.isOupUser) {
					this.isOupUser = false;
					this.oupLogout();
				}
			} catch (e) {
				this.isOupUser = false;
				// this.oupLogout();
			} finally {
				resolve(this.isOupUser);
			}
		});
	}

	public signInRedirectCallback(): void {
		this.userManager
			.signinRedirectCallback()
			.then((user: any) => {
				// console.log(user);
				// {
				// 	"id_token": "xxxid_tokenxxx",
				// 	"access_token": "xxxaccess_tokenxxx",
				// 	"refresh_token": "xxxrefresh_tokenxxx",
				// 	"token_type": "bearer",
				// 	"scope": "openid",
				// 	"profile": {
				// 		"clientID": "dev/activityplatform",
				// 		"sub": "xxx",
				// 		"userID": "yyy",
				// 		"https://oupchina.com.hk/identity_provider": "esas",
				// 		"https://oupchina.com.hk/identity_provider_user_data": {
				// 			"usertoken": 123456,
				// 			"loginID": "act_test_t1"
				// 		},
				// 		"https://oupchina.com.hk/school_id": "xxxyyy",
				// 		"https://oupchina.com.hk/school#en": "(demo) Activity School",
				// 		"https://oupchina.com.hk/school#zh-Hant": "(demo) Activity School",
				// 		"https://oupchina.com.hk/user_type": "teacher",
				// 		"https://oupchina.com.hk/name#zh-Hant": "act_test_t1",
				// 		"https://oupchina.com.hk/name#en": "act_test_t1",
				// 		"https://oupchina.com.hk/academic_year": "2024-2025",
				// 		"https://oupchina.com.hk/teacher_position": "Teacher",
				// 		"https://oupchina.com.hk/keep_me_signed_in": false,
				// 		"https://oupchina.com.hk/initial_login_client_id": "dev/activityplatform"
				// 	},
				// 	"expires_at": 1726828417
				// }

				this._oupSsoUser = user;
				this.accessToken = user.access_token;
				this.isOupUser = true;

				this.oupTokenLogin(
					user.profile["https://oupchina.com.hk/school_id"],
					user.profile.userID,
					user.profile["https://oupchina.com.hk/user_type"],
					user.access_token
				);

				// 改為由loginPostProcess處理redirect
				// if (user.state != null) {
				// 	this.router.navigateByUrl(user.state);
					// if user.state is not null
					// it carries the state set when signin start
					//
					// if the signin is triggered by userManager.signinRedirectWithESASToken
					// the user.state would be the url object of the location right before signin
				// }

			})
			.catch((error: any) => {
				console.log('login error', error)
				if (this.router.url.startsWith("/redirect")) {
					this.router.navigate(['/login']);
				}
			});

	}

	public signinRedirectWithEsaToken(): void {
		// 等userManager load完成
		this.coms.waitFor(()=>(this.userManager), 20, 1000).then(async()=>{
			  // 清除現有登入狀態
			await this.userManager.removeUser();
			sessionStorage.clear();
			this.datas.userInfo = null;
			this.datas.jwt = '';
			this.datas.jwtObj = null;
			localStorage.removeItem('common_jwt');
			localStorage.removeItem('common_uid');
			localStorage.removeItem('common_schoolId');
			localStorage.removeItem('common_schoolLogo');
			localStorage.removeItem('common_photo');
			localStorage.removeItem('homeScreen_data');


			// 由OUP iSolution出來的URL是這樣的: http://localhost:5173/deeplink?token=[TOKEN]&amp;resource=chinese
			// 而RoWeb支持直接進入工作間folder的URL是這樣的: https://dev.e-activity.oupchina.com.hk/workspace?module=licensed&packID=61
			// 所以需要OUP修改成這樣 http://localhost:4200/workspace?module=licensed&packID=61&clientId=oup&token=[TOKEN]
			// 前段是我們的workspace路徑，後面token是OUP的token，clientId是固定oup
			// 在workspace的resolver加入deeplink.resolver來處理

			const rawSearch = window.location.search; // 例如 "?module=licensed&amp;packID=61&amp;clientId=oup&amp;token=abc123"
			// 解碼 HTML 實體
			const decodedSearch = rawSearch.replace(/&amp;/g, '&');
			// 使用 URLSearchParams 解析
			const params = new URLSearchParams(decodedSearch);
			const esaToken = params.get('token')?.toLowerCase();
			// 移除指定參數
			params.delete('token');
			params.delete('clientId');
			params.delete('resource');
			const newQuery = params.toString(); // 重新組合 query string
			const state = window.location.pathname + (newQuery ? '?' + newQuery : '');
			
			// 以esaToken登入
			this.userManager.signinRedirect({
				login_hint: 'esas-token:%s:'.replace('%s', esaToken),
				state: state
			}).catch((error: any)=>{
				console.log('login error', error);
				// if (this.router.url.startsWith("/redirect")) {
				this.router.navigate(['/login']);
				// }
			});
		});

	}

	public oupTokenLogin(schoolId: string, userId: string, userType: string, token: string): void {
		if (this._isRunningOupTokenLogin) return;
		this._isRunningOupTokenLogin = true;

		let apiData: any = [
			"oup",
			{
				"school_id": schoolId,
				"user_id": userId,
				"user_type": userType,
				"token": token
			}

		];
		this.datas.post2({data: {api: 'Account.login_by', json: apiData}}).then((res: any) => {
			if (res.code == 0 && res.account) {
				this.datas.userInfo = res.account;
				if (typeof this.datas.userInfo?.personalSettings == 'string') {
					this.datas.userInfo.personalSettings = JSON.parse(this.datas.userInfo?.personalSettings);
				}
				this.datas.jwt = res.account.jwt;
				let homeLayout: any = res.webHomeLayout ? res.webHomeLayout : res.homeLayout;
				if (localStorage) {
					localStorage.setItem('common_jwt', this.datas.jwt);
					localStorage.setItem('common_uid', res.account.uid);
					localStorage.setItem('common_schoolId', res.account.school_id);
					// if (this.schoolModal) {
					// 	let found = this.schoolModal.items.find(item => item.uid == uid);
					// 	if (found && found.url) {
					// 		localStorage.setItem('common_photo', found.url);
					// 	}
					// }
					let found = res.settings.find(s => s.key == 'HOME_PAGE_THEME_COLOR');
					if (found) {
						let obj = JSON.parse(homeLayout);
						obj.homePageThemeColor = found.value.replace(/[\\\"]/g, '');
						homeLayout = JSON.stringify(obj);
					}
					let homeScreenData = {account: res.account, homeLayout: homeLayout, homeSettings: res.homeSettings};
					localStorage.setItem('homeScreen_data', JSON.stringify(homeScreenData));
				}
				try {
					this.datas.jwtObj = JSON.parse(this.datas.jwt);
				} catch (e) {
				}
				// this.storage.insertLoginRecord(res.account.uid);
				// let switchAccount = this.datas.post('RODevice.switch_account', [this.data.jwtDeviceToken, this.datas.jwt]);
				// zip(this.tg.loadSchoolYear(), switchAccount).subscribe((res: any) => {
				// 	try {
				// 		homeLayout = JSON.parse(homeLayout);
				// 	} catch (e) { }
				// 	// if (this.eruda.commands) {
				// 	// 	this.tg.resetDataifSchoolChange(); //reset school year
				// 	// 	let path = this.eruda.commands.path;
				// 	// 	this.eruda.commands = null;
				// 	// 	if (path.indexOf('login') == -1) {
				// 	// 		this.router.navigate([path]);
				// 	// 		return;
				// 	// 	}
				// 	// }
				// 	let homeScreen = 'icon';
				// 	if (homeLayout.options) {
				// 		if (homeLayout.options.main_page_mode) {
				// 			homeScreen = homeLayout.options.main_page_mode;
				// 		}
				// 	}
				// 	this.storage.redirectMain(homeScreen);
				// });
				// } else if (res.code == 10) {
				// 	this.lds.remove('load');
				// 	this.alertModal = {
				// 		type: 'confirm', msg: 'confirm-force-login', confirm: () => {
				// 			this.login(1);
				// 		}
				// 	};
				// } else if (res.code == 16) {
				// 	this.lds.remove('load');
				// 	this.als.alert('login.login-msg-16', null, { btns: [['ok']] });
				// } else if (res.code == 11) {
				// 	this.lds.remove('load');
				// 	this.als.alert('login.msg-account-not-exist', null, { btns: [['ok']] });
				// } else {
				// 	this.lds.remove('load');
				// 	this.als.alert('Error code:' + res.code, null, { btns: [['ok']] });
				// }
				// });
				if (res.message == "LoginSuccess") {
					// console.log('redirect to home-screen', this.router.url);
					if (this.router.url.startsWith("/redirect")) {
						// this.router.navigate(['../../home-screen'], {relativeTo: this.aRoute});
						this.loginPostProcess();
					}
				}
			} else {
				this.logout();
			}
			this._isRunningOupTokenLogin = false;
		});
	}

	public removeSubscription() {
		// Unsubscribe from the userInfo$ observable
		if (this.userInfoSubscription) {
			this.userInfoSubscription.unsubscribe();
			// console.log('Unsubscribed from userInfo$');
			this.userInfoSubscription = null;
		}
	}

	public oupLogout(state: string = ""): void {
		// Clear local storage
		// localStorage.clear();

		// // Clear cookies
		// const cookies = document.cookie.split(';');
		// for (let i = 0; i < cookies.length; i++) {
		//     const cookie = cookies[i];
		//     const eqPos = cookie.indexOf('=');
		//     const name = eqPos > -1 ? cookie.substr(0, eqPos) : cookie;
		//     document.cookie = name + '=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/';
		// }

		// // Clear session storage
		// sessionStorage.clear();

		// window.location.href = "login";
		// navigate('/')
		// Refresh the page
		// window.location.reload();
		this.removeSubscription();

		for (let key of this.prev_school_year_data_cache_keys) {
			sessionStorage.removeItem(key);
		}
		// sessionStorage.clear();

		if (state === 'updateUserInfoError' || state === 'renew_token_fail' || state === 'idle_timeout') {
			// redirect to login page directly
			state = '';
			this.renew_oup_user().then(success => {
				if (success) {
					const params: any = {};
					if (state) params.state = state;
					this.userManager.signoutRedirect(params);
				} else {
					// create a url to /login
					location.href = this.locationStrategy.prepareExternalUrl('/login')
				}
			});

			return;
		}
		const params: any = {};
		if (state) params.state = state;
		this.userManager.signoutRedirect(params);
	}

	public logout(prompt: string = ""): void {
		try {
			// Force clear all unfinished tasks
			this.unloadService.unfinishedBusinessService.forceClearAllTasks();

			this.datas.post('Account.logout').subscribe((res: any) => {
				setTimeout(() => { //avoid error
					this.isOupUser = false;
					this.datas.userInfo = null;
					this.datas.jwt = '';
					this.datas.jwtObj = null;
					if (localStorage) {
						localStorage.removeItem('common_jwt');
						localStorage.removeItem('common_uid');
						localStorage.removeItem('common_schoolId');
						localStorage.removeItem('common_schoolLogo');
						localStorage.removeItem('common_photo');
						sessionStorage.removeItem('common_photo_done');
						sessionStorage.removeItem('common_schoolLogo_done');
						localStorage.removeItem('homeScreen_data');
						for (let key of this.prev_school_year_data_cache_keys) {
							sessionStorage.removeItem(key);
						}
					}
					this.oupLogout(prompt);
				}, 10);
			});
		} catch (err) {
			console.error('Logout failed:', err);
			debugger;
		}
	}

	private async initIdleTimer(): Promise<void> {
		// check is in iframe
		let is_in_iframe = window.self !== window.top;
		if (is_in_iframe) {
			return;
		}
		console.log('init oup idle timer', {is_in_iframe});
		try {
			await this.coms.waitFor(() => this.datas.jwt && this.isOupUser, 300, 100);
		} catch (e) {
			return;
		}
		let idle_Time = 1000 * 60 * 60;
		let renew_Time = 1000 * 60 * 3;

		//閒置1小時自動登出 (https://openknowledge-team.monday.com/boards/7119509147/pulses/7978955915)
		let time: any;
		let idle_logout_time = Date.now() + idle_Time;
		let renew_token_time = Date.now() + 10 * 1000;

		let resetTimer: any = async () => {
			if (idle_logout_time && Date.now() > idle_logout_time) {
				logout_process("idle_timeout");
				return;
			}
			idle_logout_time = Date.now() + idle_Time;
			// console.log('reset oup idle timer');
		}

		['mousedown', 'mousemove', 'keydown', 'scroll', 'touchstart'].forEach((name: string) => {
			document.addEventListener(name, resetTimer, true);
		});


		let idle_logout_id = setInterval(async () => {
			if (!this.isOupAccount) {
				clearInterval(idle_logout_id);
				console.log('clear oup idle timer check');
				return;
			}
			if (Date.now() > renew_token_time) {
				renew_token_time = Date.now() + renew_Time;
				let success = await this.renew_oup_user();
				if (!success) {
					logout_process('renew_token_fail');
					return;
				}
			}
			// console.log('idle check', {idle_logout_time, now: Date.now()});

			if (this.datas.jwt && this.isOupUser) {
				if (`${this.datas.userInfo?.user_role}` === '2' && this.is_in_lesson) {
					resetTimer();
					return;
				}
			}
			if (Date.now() < idle_logout_time) {
				return;
			}
			logout_process('idle_timeout');
		}, 1000);

		let logout_process = async (logout_reason = null) => {
			clearInterval(idle_logout_id);
			for (let cb of this.on_logout_callbacks) {
				await cb({type: 'idle_timeout'});
			}
			['mousedown', 'mousemove', 'keydown', 'scroll', 'touchstart'].forEach((name: string) => {
				document.removeEventListener(name, resetTimer, true);
			});
			this.logout(logout_reason);
		}

	};

	async get_oup_school_year() {
		let response = await this.datas.call('OUPDataSync.get_school_year_data');
		let start_time = response.data.start_time;
		let end_time = response.data.end_time;
		this.oup_school_year = this.parse_to_oup_school_year(start_time, end_time);
		return this.oup_school_year;
	}

	parse_to_oup_school_year(start_time, end_time) {
		let start_year = moment(start_time * 1000).year();
		let end_year = moment(end_time * 1000).year();
		return `${start_year}-${end_year}`;
	}

	private async loginPostProcess() {
		if (this._isRunningLoginPostProcess || (location.port != '' && sessionStorage.getItem('oup'))) return;
		this._isRunningLoginPostProcess = true;

		try {
			await this.coms.waitFor(() => this.accessToken, 999999, 100);
			let oupUser = await this.updateUserInfo();
			await this.get_oup_school_year();
			let result = await this.mlClientApi("fetchEntitlementsByUserID", {userId: oupUser?.userID, schoolYear: this.oup_school_year});
			if (!result || !result[0]) {
				this.logout("noEntitlement");
				// let msg:string =
				// this.datas.lang == "sc" ? "你的帐户没有此产品的使用权限。如有查询,请联繫我们的客户服务。" :
				// this.datas.lang == "en" ? "You don't have rights to access this product. For enquiry, please contact our customer services." :
				// "你的帳戶沒有此產品的使用權限。如有查詢,請聯繫我們的客戶服務。";

				// let spanNodeList:NodeList = document.querySelectorAll('span.message');
				// let alertExists:boolean = false;
				// spanNodeList.forEach((node:HTMLSpanElement)=>{
				// 	if (node.innerHTML==msg) alertExists = true;
				// });
				// if (!alertExists) this.als.alert2(msg, null, { btns: [['ok']] }).then(()=>{
				// 	this.logout();
				// });
			} else {
				this.entitlement_result = result;
				if (this.router.url.startsWith("/redirect")) {
					// console.log(this.router.url)
					if (
						this._oupSsoUser &&
						this._oupSsoUser.state != null &&
						!this._oupSsoUser.state.includes("/redirect") &&
						!this._oupSsoUser.state.includes("/login") //如user在已登入狀態下按login，登入後不應再redirect回login
					) {
						this.router.navigateByUrl(this._oupSsoUser.state);
					} else {
						this.router.navigate(['../../home-screen'], {relativeTo: this.aRoute});
					}
				}
				this.checkNeedShowEula();
				return this.fetch_all_data();
			}
		} catch (e) {
			console.error(e);
		} finally {
			this._isRunningLoginPostProcess = false;
		}
	}

	// public fetchUserInfo():Promise<any> {
	// 	return new Promise((resolve:Function, reject:Function):void=>{
	// 		if (this._apiData.fetchUserInfo!=undefined) {
	// 			resolve(this._apiData.fetchUserInfo);
	// 		} else {
	// 			this.mlClient.fetchUserInfo().then((oupUser:any)=>{
	// 				this._apiData.fetchUserInfo = oupUser;
	// 				resolve(this._apiData.fetchUserInfo);
	// 			});
	// 		}
	// 	});
	// }

	public async updateUserInfo(): Promise<any> {
		if (!this.isOupAccount) return Promise.reject("notOupUser");

		try {
			// let oupUser = await this.mlClient.fetchUserInfo();
			let oupUser = await this.mlClientApi("fetchUserInfo");
			// console.log(userInfo);
			// userInfo: {
			// 	"userID": "d9ca8888-5ca2-49c8-85ac-51ebfde1e55c",
			// 	"https://oupchina.com.hk/identity_provider": "esas",
			// 	"https://oupchina.com.hk/identity_provider_user_data": {
			// 		"usertoken": 20033912,
			// 		"loginID": "act_test_t1"
			// 	},
			// 	"https://oupchina.com.hk/school_id": "5ec9a3c2-32d4-4adf-8176-41d145c2a770",
			// 	"https://oupchina.com.hk/school#en": "(demo) Activity School",
			// 	"https://oupchina.com.hk/school#zh-Hant": "(demo) Activity School",
			// 	"https://oupchina.com.hk/user_type": "teacher",
			// 	"https://oupchina.com.hk/name#zh-Hant": "act_test_t1",
			// 	"https://oupchina.com.hk/name#en": "act_test_t1",
			// 	"https://oupchina.com.hk/academic_year": "2024-2025",
			// 	"https://oupchina.com.hk/teacher_position": "Teacher",
			// 	"clientID": "dev/activityplatform",
			// 	"https://oupchina.com.hk/keep_me_signed_in": false,
			// 	"https://oupchina.com.hk/last_login_client_id": "dev/activityplatform",
			// 	"https://oupchina.com.hk/initial_login_client_id": "dev/activityplatform",
			// 	"sub": "d9ca8888-5ca2-49c8-85ac-51ebfde1e55c"
			// }
			if (this.datas.userInfo) {

				this.datas.userInfo.ename = oupUser["https://oupchina.com.hk/name#en"];
				this.datas.userInfo.cname = oupUser["https://oupchina.com.hk/name#zh-Hant"];
				this.datas.userInfo.first_name = oupUser["https://oupchina.com.hk/name#en"];
				this.datas.userInfo.c_first_name = oupUser["https://oupchina.com.hk/name#zh-Hant"];
				this.datas.userInfo.last_name = "";
				this.datas.userInfo.c_last_name = "";
				this.datas.userInfo.gender = 1;
				this.datas.user_role = oupUser["https://oupchina.com.hk/teacher_position"] == "Teacher" ? 3 : 2;

				if (this.datas.getLang() == "en") {
					this.datas.userInfo.school.title = oupUser["https://oupchina.com.hk/school#en"];
					this.datas.userInfo.school_name = oupUser["https://oupchina.com.hk/school#en"];
				} else {
					this.datas.userInfo.school.title = oupUser["https://oupchina.com.hk/school#zh-Hant"];
					this.datas.userInfo.school_name = oupUser["https://oupchina.com.hk/school#en"];
				}

				//供OUP用
				this.datas.userInfo.oup = oupUser;

				return this.datas.userInfo.oup;
			}
		} catch (e) {
			console.error(e);
			// debugger;
			this.logout("updateUserInfoError");
		}
	}

	async fetch_school() {
		this.oup_school = await this.mlClientApi("fetchSchoolByID", {schoolId: this.datas.userInfo?.oup["https://oupchina.com.hk/school_id"]});
	}

	get_school_name() {
		let lang = this.datas.lang;
		// 如果已取得oup_school，直接取名稱
		if (this.oup_school) {
			if (['sc', 'tc'].includes(lang)) {
				return this.oup_school.nameChi || '';
			} else if (['en'].includes(lang)) {
				return this.oup_school.nameEng || '';
			} else {
				return '';
			}
			// 否則可試取userInfo的oup的school名稱
		} else if (this.datas && this.datas.userInfo && this.datas.userInfo?.oup) {
			if (['sc', 'tc'].includes(lang)) {
				return this.datas.userInfo?.oup["https://oupchina.com.hk/school#zh-Hant"] || '';
			} else if (['en'].includes(lang)) {
				return this.datas.userInfo?.oup["https://oupchina.com.hk/school#en"] || '';
			} else {
				return '';
			}
		}
		return '';
	}

	async fetch_entitlements() {
		let oupUser = this.datas.userInfo?.oup;
		//取得User的Entitlements
		this.entitlement_result = await this.mlClientApi("fetchEntitlementsByUserID", {userId: oupUser.userID, schoolYear: this.oup_school_year});
	}

	async fetch_teachers() {
		this.teachers = await this.mlClientApi("fetchTeachersBySchoolID", {schoolId: this.datas.userInfo?.oup["https://oupchina.com.hk/school_id"]});
		await Promise.all(this.teachers.map(async (t) => {
			let teacher_class = await this.mlClientApi("fetchTeacherClasses", {userId: t.userID});
			// console.log('teacher_class', teacher_class);
			t.classes = teacher_class;
			this.teacher_map[t.userID] = t;
		}));
		return this.teachers;
	}

	async fetch_classes(school_year = null) {
		let classes = await this.mlClientApi("fetchClassesBySchoolID", {
			schoolId: this.datas.userInfo?.oup["https://oupchina.com.hk/school_id"],
			year: school_year ? school_year : this.oup_school_year
		});
		// sort by name
		classes.sort((a, b) => {
			let nameA = a.nameEng || a.nameChi;
			let nameB = b.nameEng || b.nameChi;
			return nameA.localeCompare(nameB);
		});
		classes.forEach((c) => {
			this.class_map[c.classID] = c;
		});
		if (school_year === null) {
			this.classes = classes;
		}
		return classes;
	}

	async fetch_students(classes = null) {
		let is_class_empty = classes === null;
		if (is_class_empty) {
			classes = this.classes;
		}
		let students = [];
		let p_list = classes.map(async (c) => {
			c.students = await this.mlClientApi("fetchStudentsByClassID", {classId: c.classID});
			c.students = c.students.filter(s => !!s);
		});

		await Promise.all(p_list);
		classes.forEach((c) => {
			students = students.concat(c.students);
		});
		// filter duplicate students
		let map = {};
		students = students.filter(s => !!s).filter((s) => {
			if (map[s.userID]) {
				return false;
			}
			map[s.userID] = true;
			if (!this.student_map[s.userID]) {
				this.student_map[s.userID] = s;
			}
			return true;
		});
		if (is_class_empty) {
			this.students = students;
		}
		return students;
	}

	public async get_last_sync_hash() {
		let response = await this.datas.call('OUPDataSync.get_last_sync_hash');
		if (response.code == 0) {
			this.last_sync_hash = response.hash;
		} else {
			this.last_sync_hash = null;
		}
	}

	public async fetch_all_data() {
		if (this._is_fetching_all_data) {
			return;
		}
		if (this._is_fetching_all_data_done) {
			return;
		}
		this._is_fetching_all_data = true;
		this._is_fetching_all_data_done = false;

		await this.coms.waitFor(() => this.accessToken, 9999999, 100);

		await Promise.all([
			// Fetch last_sync_hash and userInfo concurrently
			this.get_last_sync_hash(),
			this.datas.userInfo && this.datas.userInfo?.oup
			? Promise.resolve()
			: this.updateUserInfo().then(() => {
				// Ensure userInfo.oup is available
				if (!this.datas.userInfo || !this.datas.userInfo?.oup) {
					throw new Error('Failed to fetch userInfo.oup');
				}
			}),
			// Fetch oup_school_year if not already set
			this.oup_school_year ? Promise.resolve() : this.get_oup_school_year(),
		]);

		let school_id = this.datas.userInfo?.oup["https://oupchina.com.hk/school_id"];
		let session_key = `oup_data_${school_id}_${this.oup_school_year}`;
		let old_data = sessionStorage.getItem(session_key);
		if (!old_data) {
			let id = Math.random().toString(36).substring(2, 15);

			await this.coms.waitFor(() => this.datas.userInfo && this.datas.userInfo?.oup, 99999999, 10);
			console.log('fetch_all_data start call api', id, this.datas.userInfo?.oup);

			await Promise.all([
				this.fetch_school(),
				this.fetch_entitlements(),
				this.fetch_teachers(),
				this.fetch_classes(),
			]);

			await this.fetch_students();
			await this.sync_all_data();
			sessionStorage.setItem(session_key, JSON.stringify({
				teachers: this.teachers,
				classes: this.classes,
				students: this.students,
				entitlement_result: this.entitlement_result,
				teacher_map: this.teacher_map,
				class_map: this.class_map,
				student_map: this.student_map,
			}));
		} else {
			let data = JSON.parse(old_data);
			this.teachers = data.teachers || [];
			this.classes = data.classes || [];
			this.students = data.students || [];
			this.entitlement_result = data.entitlement_result || [];
			this.teacher_map = data.teacher_map || {};
			this.class_map = data.class_map || {};
			this.student_map = data.student_map || {};
			this.user_id_map = await this.get_user_id_map();
		}

		await this.fetch_old_data();

		// console.log('fetch_all_data done', this);
		this._is_fetching_all_data = false;
		this._is_fetching_all_data_done = true;
		this._is_fetched_once = true;

		this.prev_school_year_data_cache_keys.push(session_key);
	}

	public async sync_all_data() {
		let teachers = this.teachers;
		let classes = this.classes;
		let students = this.students;

		let data = {
			teachers,
			classes,
			students,
		};
		console.log('sync_all_data', this);
		let data_json = JSON.stringify(data);
		// md5 data
		let hash = Md5.hashStr(data_json);

		let last_sync_hash = this.last_sync_hash;
		if (last_sync_hash !== hash) {
			let response = await this.datas.call('OUPDataSync.sync', data_json, hash);
			if (response.code !== 0) {
				console.error('sync_all_data error', response);
				return;
			}
			// localStorage.setItem('last_sync_hash', hash);
		}
		this.user_id_map = await this.get_user_id_map();
	}

	public async get_user_id_map(oup_user_ids = null) {
		let response = await this.datas.call('OUPDataSync.get_user_id_map', oup_user_ids);

		if (response.code !== 0) {
			console.error('get_user_id_map error', response);
			return;
		}

		return response.map;
	}

	public get_user_display_name(uid) {
		let lang = this.datas.lang;
		//如果是自己，試試直接取oup的userinfo
		if (uid && this.datas && this.datas.userInfo && (uid == this.datas.userInfo?.uid)) {
			if (this.datas.userInfo?.oup) {
				if (['sc', 'tc'].includes(lang)) {
					return this.datas.userInfo?.oup["https://oupchina.com.hk/name#zh-Hant"] || '';
				} else if (['en'].includes(lang)) {
					return this.datas.userInfo?.oup["https://oupchina.com.hk/name#en"] || '';
				}
			}
		}

		let oup_user_id = this.user_id_map[uid];
		if (!oup_user_id) {
			return '';
		}
		let user = null;
		if (this.teacher_map[oup_user_id]) {
			user = this.teacher_map[oup_user_id];
		}
		if (this.student_map[oup_user_id]) {
			user = this.student_map[oup_user_id];
		}

		if (!user) {
			return '';
		}

		if (['sc', 'tc'].includes(lang)) {
			return user.nameChi;
		}
		if (['en'].includes(lang)) {
			return user.nameEng;
		}

		return '';
	}

	public get_user_class_num(uid) {
		let oup_user_id = this.user_id_map[uid];
		if (!oup_user_id) return '';

		let user = this.teacher_map[oup_user_id] || this.student_map[oup_user_id] || null;
		if (!user) return '';

		return (isNaN(parseInt(user.classNo))) ? '' : parseInt(user.classNo);
	}

	public get_user_class_name(uid) {
		let oup_user_id = this.user_id_map[uid];
		if (!oup_user_id) return '';

		let user = this.teacher_map[oup_user_id] || this.student_map[oup_user_id] || null;
		if (!user) return '';

		let class_id = user.classID;
		if (!class_id) return '';

		return this.get_class_name(class_id);
	}
	/**
	 *
	 * @param {string[]|string} class_ids
	 */
	get_class_name(class_ids: string[] | string) {
		let input = class_ids;
		
		if (typeof class_ids === 'string') {
			class_ids = [class_ids];
		}
		let lang = this.datas.lang;

		if (!class_ids) {
			class_ids = [];
		}

		let class_name = class_ids
			.map((class_id) => {
				if (this.class_map[class_id]) {
					if (['sc', 'tc'].includes(lang)) {
						return this.class_map[class_id].nameChi;
					} else if (['en'].includes(lang)) {
						return this.class_map[class_id].nameEng;
					}
				}
				return null;
			})
			.filter((name) => !!name)
			.join(',');

		if (!class_name && typeof input === 'string') {
			return input
		}
		return class_name;

	}

	get_lesson_name(uid) {
		let display_name = this.get_user_display_name(uid);
		return {
			tc: `${display_name}的課堂`,
			sc: `${display_name}的课堂`,
			en: `${display_name}'s Lesson`,
		}[this.datas.lang];
	}

	on_logout(cb) {
		this.on_logout_callbacks.push(cb);

		return () => {
			this.on_logout_callbacks = this.on_logout_callbacks.filter((c) => c !== cb);
		}
	}

	public checkNeedShowEula(): Promise<boolean> {
		return new Promise((resolve, reject) => {
			if (!this.isOupUser || (this.isOupUser && this._isNoEntitlement)) return resolve(false);
			this.coms.waitFor(() => this.datas.jwt, 10, 1000).then(() => {
				this.datas.post2({data: {api: 'Account.getUpdateSetting', json: []}}).then((res: any) => {
					const routeToEula: Function = (): void => {
						// 因為其他service或resolver可能會route至其他module，所以需要在這裡欄截router event
						// 在eula.component.ts內在accept eula後，會unscripte這些subscriptions
						let activeSubScription: Subscription = this.routerSubscriptions.find((sub: any) => !sub.closed);
						if (!activeSubScription) this.routerSubscriptions.push( //如果沒有active的subscription，則新增一個
							this.router.events.subscribe((event: any) => {
								if (event instanceof NavigationStart && event.url !== '/eula') {
									console.log('請先同意EULA協議');
									this.router.navigate(['eula']); // 強制停留在 EULA 頁面
								}
							})
						);
						//暫存當前路由，等EULA同意後再跳轉
						this.oupEulaRouteTemp = this.router.url || '/home-screen';
						if (
							this.oupEulaRouteTemp.startsWith("/redirect") ||
							this.oupEulaRouteTemp.startsWith("/login") ||
							this.oupEulaRouteTemp.startsWith("/eula")
						) this.oupEulaRouteTemp = '/home-screen';
						this.router.navigate(['eula']); // route to EULA page
						resolve(true);
					}

					if (!res.account.personalSettings) { //初始時personalSettings可能是NULL
						routeToEula();
					} else { //如果有personalSettings，檢查是否需要顯示EULA
						try {
							let ps: any = JSON.parse(res.account.personalSettings);
							if (!ps.oupEulaVer || ps.oupEulaVer < this.oupEulaVer) {
								routeToEula();
							} else {
								resolve(false);
							}
						} catch (e) {
							resolve(false);
						}
					}
				});
			}).catch(() => {
				resolve(false);
			});
		});
	}

	/*public initEula():void {
		this.isOupUser$.subscribe((isOupUser:boolean)=>{
			if (isOupUser) {
				this.checkNeedShowEula();
			}
		});
	}*/

	public async get_role_data() {
		// this.datas.post('ROWorkspace.get_user_role', []).subscribe((res: any) => {
		// 	this.role_data = res
		// })
		this.role_data = await this.datas.post('ROWorkspace.get_user_role', []).toPromise()
	}

	public isOupCreator() {
		// if (this.datas.userInfo.school_id == 272) {
		// 	return true
		// }
		if (this.datas.userInfo?.school.prefix == 'oup') {
			if (this.role_data.role[0] && ["OUP Editor (Creator)"].indexOf(this.role_data.role[0].tcName) != -1) {
				return true
			}
		}
		return false
	}
	public isOupApprover() {
		// if (this.datas.userInfo.school_id == 272) {
		// 	return true
		// }
		if (this.datas.userInfo?.school.prefix == 'oup') {
			if (this.role_data.role[0] && ["OUP Editor (Approver)"].indexOf(this.role_data.role[0].tcName) != -1) {
				return true
			}
		}
		return false
	}

	async fetch_old_data() {
		let response = await this.datas.call('OUPDataSync.get_prev_school_year_list');
		if (response.code !== 0) {
			return;
		}
		let list = response.data;
		this.prev_school_year_data_cache_keys = [];
		for (let school_year of list) {
			let oup_school_year = this.parse_to_oup_school_year(school_year.start_time, school_year.end_time);
			if (oup_school_year == this.oup_school_year) {
				continue;
			}
			let school_id = this.datas.userInfo?.oup["https://oupchina.com.hk/school_id"];
			let session_key = `oup_old_data_${school_id}_${oup_school_year}`;
			this.prev_school_year_data_cache_keys.push(session_key);
			if (!sessionStorage.getItem(session_key)) {
				let classes = await this.fetch_classes(oup_school_year);
				let students = await this.fetch_students(classes);
				let data = {
					classes,
					students,
				}
				sessionStorage.setItem(session_key, JSON.stringify(data));
			}
			let data = JSON.parse(sessionStorage.getItem(session_key));
			for (let c of data.classes) {
				this.class_map[c.classID] = c;
			}
			for (let s of data.students) {
				if (!this.student_map[s.userID]) {
					this.student_map[s.userID] = s;
				}
			}
		}
	}

	async renew_oup_user() {
		try {
			let renew_user = await this.userManager.signinSilent();
			if (renew_user && !renew_user.expired) {
				this.accessToken = renew_user.access_token;
				return true;
			}
			return false;
		} catch (e) {
			return false;
		}
	}

	// TODO: remove 
	async add_oup_footer_to_excel(blob) {

		return blob;
		const read_file = (blob, type) => {
			return new Promise((resolve, reject) => {
				const reader = new FileReader();

				reader.onload = async (e) => {
					resolve(e.target.result); // This is the ArrayBuffer
				};

				if (type === 'arraybuffer') {
					reader.readAsArrayBuffer(blob);
				} else if (type === 'dataurl') {
					reader.readAsDataURL(blob);
				} else if (type === 'text') {
					reader.readAsText(blob);
				} else if (type === 'binarystring') {
					reader.readAsBinaryString(blob);
				}
			});
		}

		await this.script.load('js/exceljs.min.js');
		const ExcelJS = window['ExcelJS'];
		let array_buffer = await read_file(blob, 'arraybuffer');
		const workbook = new ExcelJS.Workbook();
		await workbook.xlsx.load(array_buffer); // Load the workbook from the buffer

		// console.log('add_oup_footer_to_excel', workbook);
		let response = await fetch('assets/oup/oup_print_footer.png');
		let footer_blob = await response.blob();


		const footer_image_id = workbook.addImage({
			base64: await read_file(footer_blob, 'dataurl'),
			extension: 'png',
		});

		// get all worksheets
		workbook.worksheets.forEach((worksheet) => {
			let last_row = worksheet.lastRow.number;
			// console.log('add_oup_footer_to_excel worksheet', {last_row, worksheet});
			worksheet.addImage(footer_image_id, {
				tl: {col: 0, row: last_row + 1},
				ext: {width: 897, height: 48}
			});
		});


		const buffer = await workbook.xlsx.writeBuffer();

		return new Blob([buffer], {type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"});
	}

	appendFooterRow(ws: XLSX.WorkSheet) {
		// Decode current range
		const range = XLSX.utils.decode_range(ws['!ref']);
		const newRow = range.e.r + 1;
		
		// Add the footer cell
		const cellRef = XLSX.utils.encode_cell({ c: 0, r: newRow });
		ws[cellRef] = {
			v: "© Oxford University Press. All rights reserved.",
			t: "s",
			s: {
				font: { bold: true, color: { rgb: "FFFFFF" } },
				alignment: { horizontal: "left" },
				fill: { fgColor: { rgb: "011E41" } }
			}
		};
		
		// Extend the range
		range.e.r = newRow;
		ws['!ref'] = XLSX.utils.encode_range(range);
		
		// merge across all columns
		ws['!merges'] = ws['!merges'] || [];
		ws['!merges'].push({
			s: { r: newRow, c: 0 },
			e: { r: newRow, c: range.e.c }
		});

		// Set row height to 30 points 
		ws['!rows'] = ws['!rows'] || []; 
		ws['!rows'][newRow] = { hpt: 25 };
	} 

	async get_student_grouping_list(uid = null) {
		if (uid == null) {
			uid = this.datas.userInfo.uid;
		}

		let response = await this.datas.call('OUPGrouping.get_student_grouping_list', uid);
		return response.grouping_list.filter(g => g.child.some(c => c.members.some(m => m.uid == this.datas.userInfo.uid)));
	}

	async get_student_not_exist_grouping_list(uid = null) {
		if (uid == null) {
			uid = this.datas.userInfo.uid;
		}

		let response = await this.datas.call('OUPGrouping.get_student_grouping_list', uid);
		return response.grouping_list.filter(g => g.child.every(c => !c.members.some(m => m.uid == this.datas.userInfo.uid)));
	}
}

class MiddleLayerApiQueue {

	private queue = [];
	private is_running = false;
	private running_tasks = [];

	constructor() {
		this.run();
	}

	public add(id) {
		return new Promise((resolve, reject) => {
			this.queue.push({
				id, resolve,
			});
			this.run();
		});
	}

	async run() {
		if (this.is_running) {
			return;
		}
		this.is_running = true;
		console.log('MiddleLayerApiQueue run started');

		// 正在執行的任務最多5個，一個完成就接著執行下一個，直到queue清空
		// 外面會使用 add 方法加入任務，並等待Promise resolve
		// 外面完成任務後會呼叫 finish 方法通知此class
		while (this.queue.length > 0 || this.running_tasks.length > 0) {
			if (this.running_tasks.length >= 5) {
				await this.timeout(100);
				continue;
			}
			if (this.queue.length > 0) {
				let task = this.queue.shift();
				this.running_tasks.push(task);
				task.resolve();
			} else {
				await this.timeout(100);
				continue;
			}
		}
		this.is_running = false;
		console.log('MiddleLayerApiQueue run finished');
	}

	finish(id) {
		let index = this.running_tasks.findIndex(item => item.id == id);
		if (index != -1) {
			this.running_tasks.splice(index, 1);
		}
	}

	timeout(ms) {
		return new Promise(resolve => setTimeout(resolve, ms));
	}

}